import { AuthApi } from '@api'
import { useGuardedNavigate } from '@hook'
import { useAccessSidebar } from '@hook/useAccessSidebar'
import { useUserRole } from '@hook/useUserRole'
import { LockIcon, LogoutIcon, SettingsIcon } from '@icons'
import { palette } from '@themes'
import { useSidebarStore } from '@zustore/store'
import { Nav } from 'grommet'
import { Chat } from 'grommet-icons'
import { ButtonHTMLAttributes, ReactNode, useMemo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { APP_ROUTES_APP_PHARMACY_WORKSPACE } from '../../../constants/routes'
import { SmallText } from '../Text'

export const style = {
  alignItems: 'center',
  color: 'white',
  display: 'inline-flex',
  gap: '20px',
  padding: '12px 6px',
  textDecoration: 'none'
}

type SidebarButtonProps = {
  icon?: ReactNode
  label?: any
  to?: string
  className?: string
  isDropDown?: boolean
  children?: ReactNode
  permission?: string | string[]
  /** Visible but not navigable (e.g. Coming soon) */
  disabled?: boolean
} & Omit<React.ComponentProps<typeof NavLink>, 'to'>

/** Pharmacy Workspace uses one pathname + ?pharmTab= — match on tab, not path alone. */
const isPharmacyWorkspaceLink = (to: string) => to.startsWith(APP_ROUTES_APP_PHARMACY_WORKSPACE)

const pharmacyWorkspaceTabActive = (to: string, pathname: string, search: string) => {
  if (pathname !== APP_ROUTES_APP_PHARMACY_WORKSPACE) return false
  const targetTab = new URLSearchParams(to.split('?')[1] || '').get('pharmTab')
  const currentTab = new URLSearchParams(search).get('pharmTab') || 'Catalog'
  return Boolean(targetTab) && targetTab === currentTab
}

export const SidebarButton = ({
  icon,
  label = '',
  to = 'blank',
  className = '',
  children,
  isDropDown,
  permission,
  disabled = false,
  ...rest
}: SidebarButtonProps) => {
  const { collapsed } = useSidebarStore()
  const { hasRole } = useUserRole()
  const { guardedNavigate } = useGuardedNavigate()
  const location = useLocation()
  const hasAccess = useAccessSidebar(permission ?? '')
  const superAdmin = hasRole('super_admin')

  const linkActive = useMemo(() => {
    if (disabled) return false
    if (isPharmacyWorkspaceLink(to)) {
      return pharmacyWorkspaceTabActive(to, location.pathname, location.search)
    }
    const pathOnly = to.split('?')[0]
    if (pathOnly === '/' || pathOnly === '') {
      return location.pathname === '/' || location.pathname === ''
    }
    return location.pathname === pathOnly || location.pathname.startsWith(`${pathOnly}/`)
  }, [disabled, to, location.pathname, location.search])

  if (permission != null) {
    if (!hasAccess && !superAdmin) {
      return null
    }
  }

  return (
    <NavLink
      aria-disabled={disabled || undefined}
      className={`sidebar-btn ${className} ${disabled ? 'pointer-events-none opacity-50 cursor-not-allowed' : 'hover:bg-[#121D10]'}`}
      tabIndex={disabled ? -1 : undefined}
      to={disabled ? '#' : to}
      {...rest}
      onClick={event => {
        event.preventDefault()
        if (disabled) return
        if (to) {
          guardedNavigate(to)
        }
        if (rest.onClick) {
          rest.onClick(event)
        }
      }}
      style={() =>
        linkActive && (!collapsed || isDropDown)
          ? { ...style, background: '#121D10', borderLeft: `3px solid ${palette.asparagus}` }
          : linkActive && collapsed
            ? { background: '#3B6435', borderRadius: '50%', marginInline: 'auto' }
            : linkActive && collapsed && isDropDown
              ? { ...style, background: '#121D10', borderLeft: `3px solid ${palette.asparagus}` }
              : style
      }
    >
      {icon && <span className={`sidebar-icon ${collapsed ? 'scale-125' : ''}`}>{icon}</span>}
      {label && <SmallText>{label}</SmallText>}
      {children}
    </NavLink>
  )
}

export const FooterButton = ({
  icon = <Chat />,
  label = '',
  onClick,
  ...rest
}: { icon: ReactNode; label: string } & ButtonHTMLAttributes<HTMLButtonElement>) => {
  const { collapsed } = useSidebarStore()
  return (
    <button
      className="footer-btn"
      onClick={onClick}
      {...rest}
      style={{
        alignItems: 'center',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        fontSize: '14px',
        gap: '8px'
      }}
    >
      <span className={`sidebar-icon ${collapsed ? 'scale-125' : ''}`}>{icon}</span>

      {!collapsed && label}
    </button>
  )
}

export const SidebarFooter = () => {
  const { collapsed } = useSidebarStore()
  const { logout } = AuthApi()

  return (
    <Nav
      align="start"
      aria-label="sidebar footer"
      background="asparagus"
      className={collapsed ? 'sidebar-footer-collapsed' : ''}
      gap="small"
      pad={{ left: '30px', right: 'medium', vertical: 'medium' }}
      round={{ corner: 'bottom' }}
      style={{
        color: 'white',
        justifyContent: collapsed ? 'center' : 'flex-start',
        transition: 'all 0.3s'
      }}
    >
      <FooterButton
        icon={<SettingsIcon color="white" height={'16px'} width={'16px'} />}
        label={collapsed ? '' : 'Settings'}
        style={{ fontSize: '14px' }}
      />
      <FooterButton icon={<LockIcon />} label={collapsed ? '' : 'Edit Password'} style={{ fontSize: '14px' }} />
      <FooterButton
        icon={<LogoutIcon />}
        label={collapsed ? '' : 'Logout'}
        onClick={logout}
        style={{ fontSize: '14px' }}
      />
    </Nav>
  )
}
